import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Table,
    Spinner,
    Pagination,
    PaginationItem,
    PaginationLink,
    Button,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { apiURL1 } from '../../config';
import Header from '../../components/Headers/Header';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { FaDownload, FaPrint } from 'react-icons/fa';
import './StudentResultPage.css';
import Footer from 'components/Footers/Footer';
import {
    createLandscapePdf,
    standardAutoTableOptions,
    drawCenteredFooter,
} from '../../utils/pdfLayout';

const baseURL = apiURL1;

function StudentResultPage() {
    const [studentId, setStudentId] = useState('');
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 5;
    const [madrasaInfo, setMadrasaInfo] = useState({});
    const token = localStorage.getItem("token");


    useEffect(() => {
        // Retrieve user info from localStorage
        const user = localStorage.getItem('userInfo');
        if (user) {
            try {
                const parsedUser = JSON.parse(user);
                setStudentId(parsedUser._id);
            } catch (error) {
                console.error('Error parsing user info from localStorage:', error);
                toast.error('Error retrieving student ID.');
            }
        } else {
            toast.error('User info not found in localStorage.');
        }
    }, []);

    useEffect(() => {
        fetchMadrasaInfo();
    }, []);

    useEffect(() => {
        if (studentId) {
            fetchResults();
        }
    }, [studentId, page]);

    const fetchMadrasaInfo = async () => {
        try {
            const response = await axios.get(`${baseURL}/student/school-settings`, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            }
            ); const data = response.data;
            if (data && data.length > 0) {
                setMadrasaInfo(data[0]);
            }
        } catch (error) {
            console.error('Error fetching madrasa info:', error);
        }
    };

    const fetchResults = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${baseURL}/student/studentResult/${studentId}?page=${page}&limit=${itemsPerPage}`, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            });
            // console.log('response.data.data is ', response.data.data)
            if (response.data.data.length === 0) {
                toast.warn('No results found.');
                return;

            }
            if (response.data && response.data.success && response.data.data) {
                setResults(response.data);
                setTotalPages(response.data.pagination.totalPages);
            } else {
                setResults(null);
                setTotalPages(0);
                toast.warn(response.data.message || 'No results found.');
            }
        } catch (error) {
            console.error('Error fetching results:', error);
            toast.error('Error fetching results');
            setResults(null);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    };

    const generatePdfReport = () => {
        if (!results || !results.data) return;

        const studentData = results.data;
        const pdf = createLandscapePdf(jsPDF);
        const pageWidth = pdf.internal.pageSize.getWidth();

        const img = new Image();
        img.src = "https://res.cloudinary.com/djg7v9dna/image/upload/v1742946371/mi0wavjsehps20kdzaet.png";
        try {
            pdf.addImage(img, 'PNG', 12, 8, 22, 22);
        } catch (e) { /* optional */ }

        pdf.setFontSize(13);
        pdf.setFont('helvetica', 'bold');
        pdf.text(madrasaInfo.schoolName || 'Madrasa Name', 40, 14);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text(madrasaInfo.schoolAddress || 'Madrasa Address', 40, 20);
        pdf.text(`Tel: ${madrasaInfo.schoolPhoneNumber || 'Phone'} | Email: ${madrasaInfo.schoolEmail || 'Email'}`, 40, 25);

        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`Student Result`, pageWidth / 2, 34, { align: 'center' });

        let y = 42;
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Name: ${studentData.fullName}  |  Reg: ${studentData.registrationNumber}  |  Rank: ${studentData.rank ?? '-'}`, 12, y);
        y += 8;

        studentData.results.forEach((result) => {
            if (y > pdf.internal.pageSize.getHeight() - 40) {
                pdf.addPage();
                y = 15;
            }
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.text(`${result.type} ${result.year} — Class ${result.class} | Total ${result.totalMarks} | Obtained ${result.obtainedMarks} | Grade ${result.overallGrade} | ${result.overallPercentage}%`, 12, y);
            y += 5;

            const tableData = (result.result || []).map((subject) => [
                subject?.subjectName || 'N/A',
                subject?.subjectCode || 'N/A',
                `${subject?.midSubjectObtainedMarks ?? subject?.outsideObtained ?? '-'}/${subject?.midSubjectTotalMarks ?? subject?.subjectTotalMarks ?? '-'}`,
                `${subject?.subjectObtainMarksFinal ?? '-'}/${subject?.subjectTotalMarksFinal ?? '-'}`,
                subject?.grandTotal ?? subject?.totalMarks ?? '-',
                subject?.grandObtained ?? subject?.obtainedMarks ?? '-',
                subject?.grade || 'N/A',
            ]);

            pdf.autoTable(standardAutoTableOptions({
                head: [['Subject', 'Code', 'Mid', 'Final', 'Total', 'Obtained', 'Grade']],
                body: tableData,
                startY: y,
                columnStyles: {
                    0: { cellWidth: 55, halign: 'left' },
                    1: { cellWidth: 22 },
                    2: { cellWidth: 28 },
                    3: { cellWidth: 28 },
                    4: { cellWidth: 24 },
                    5: { cellWidth: 26 },
                    6: { cellWidth: 20 },
                },
            }));
            y = pdf.lastAutoTable.finalY + 10;
        });

        drawCenteredFooter(pdf, madrasaInfo.schoolName);
        pdf.save(`student-result-${studentData.fullName}.pdf`);
    };

    const handlePrint = () => {
        if (!results || !results.data) return;

        const studentData = results.data;
        const tableStyle = `border-collapse: collapse; width: 100%; margin: 10px 0;`;
        const thStyle = `background-color: #2980b9; color: white; padding: 8px; text-align: left; border: 1px solid #ddd;`;
        const tdStyle = `padding: 8px; text-align: left; border: 1px solid #ddd;`;

        let printContent = `
            <html>
            <head>
                <title>Student Result - ${studentData.fullName}</title>
                <style>
                    body { font-family: Arial, sans-serif; margin: 20px; }
                    .header { text-align: center; margin-bottom: 20px; }
                    .school-name { font-size: 24px; font-weight: bold; }
                    .school-info { font-size: 14px; }
                    .result-title { font-size: 20px; margin-bottom: 10px; }
                    table { ${tableStyle} }
                    th { ${thStyle} }
                    td { ${tdStyle} }
                    .footer { text-align: center; margin-top: 20px; font-size: 12px; }
                    .exam-details { margin-top: 10px; }
                </style>
            </head>
            <body>
                <div class="header">
                    <img src="https://res.cloudinary.com/djg7v9dna/image/upload/v1739198257/Logo/kgamz0yjfr9uy8dsbnur.webp" alt="Madrasa Logo" width="100"/>
                    <div class="school-name">${madrasaInfo.schoolName || 'Madrasa Name'}</div>
                    <div class="school-info">
                        ${madrasaInfo.schoolAddress || 'Madrasa Address'}<br/>
                        Tel: ${madrasaInfo.schoolPhoneNumber || 'Phone'} | Email: ${madrasaInfo.schoolEmail || 'Email'}
                    </div>
                </div>
                <div class="result-title">Student Result - ${studentData.fullName}</div>
                <div><strong>Name:</strong> ${studentData.fullName}</div>
                <div><strong>Registration Number:</strong> ${studentData.registrationNumber}</div>
                <div><strong>Rank:</strong> ${studentData.rank}</div>
        `;

        studentData.results.forEach(result => {
            printContent += `
                <div class="exam-details">
                    <div><strong>Exam Type:</strong> ${result.type}</div>
                    <div><strong>Year:</strong> ${result.year}</div>
                    <div><strong>Class:</strong> ${result.class}</div>
                    <div><strong>Total Marks:</strong> ${result.totalMarks}</div>
                    <div><strong>Obtained Marks:</strong> ${result.obtainedMarks}</div>
                    <div><strong>Overall Grade:</strong> ${result.overallGrade}</div>
                    <div><strong>Overall Percentage:</strong> ${result.overallPercentage}</div>
                    
                    <table border="1">
                        <thead>
                            <tr>
                                ${(() => {
                                    const isComponent = Array.isArray(result.result) && result.result.some(s => Array.isArray(s.components) && s.components.length > 0);
                                    if (!isComponent) {
                                        return `
                                            <th>Subject Name</th>
                                            <th>Subject Code</th>
                                            <th>Total Marks</th>
                                            <th>Obtained Marks</th>
                                            <th>Grade</th>
                                            <th>Remarks</th>
                                        `;
                                    }
                                    const set = new Set();
                                    result.result.forEach(subject => {
                                        (subject.components || []).forEach(c => { const n = c?.component?.name || c?.name || c?.componentName; if (n) set.add(n); });
                                        (subject.componentObtained || []).forEach(c => { const n = c?.component?.name || c?.name || c?.componentName; if (n) set.add(n); });
                                    });
                                    const compHeaders = Array.from(set).map(n => `<th>${n}</th>`).join('');
                                    return `
                                        <th>Subject Name</th>
                                        <th>Subject Code</th>
                                        <th>Subject Obtained</th>
                                        ${compHeaders}
                                        <th>Total Marks</th>
                                        <th>Total Obtained</th>
                                        <th>Grade</th>
                                        <th>Remarks</th>
                                    `;
                                })()}
                            </tr>
                        </thead>
                        <tbody>
                            ${(() => {
                                const isComponent = Array.isArray(result.result) && result.result.some(s => Array.isArray(s.components) && s.components.length > 0);
                                if (!isComponent) {
                                    return result.result.map(subject => {
                                        const totalMarks = subject?.grandTotal ?? subject?.subjectTotalMarks ?? subject?.totalMarks ?? result?.totalMarks ?? '-';
                                        const obtainedMarks = subject?.grandObtained ?? subject?.outsideObtained ?? subject?.obtainedMarks ?? result?.obtainedMarks ?? '-';
                                        return `
                                            <tr>
                                                <td style="${tdStyle}">${subject?.subjectName || 'N/A'}</td>
                                                <td style="${tdStyle}">${subject?.subjectCode || 'N/A'}</td>
                                                <td style="${tdStyle}">${totalMarks}</td>
                                                <td style="${tdStyle}">${obtainedMarks}</td>
                                                <td style="${tdStyle}">${subject?.grade || 'N/A'}</td>
                                                <td style="${tdStyle}">${subject?.remarks || '-'}</td>
                                            </tr>
                                        `;
                                    }).join('');
                                }
                                const set = new Set();
                                result.result.forEach(subject => {
                                    (subject.components || []).forEach(c => { const n = c?.component?.name || c?.name || c?.componentName; if (n) set.add(n); });
                                    (subject.componentObtained || []).forEach(c => { const n = c?.component?.name || c?.name || c?.componentName; if (n) set.add(n); });
                                });
                                const names = Array.from(set);

                                return result.result.map(subject => {
                                    const totals = new Map();
                                    (subject.components || []).forEach(c => { const n = c?.component?.name || c?.name || c?.componentName; if (n) totals.set(n, c?.totalMarks ?? '-'); });
                                    const obtained = new Map();
                                    (subject.componentObtained || []).forEach(c => { const n = c?.component?.name || c?.name || c?.componentName; if (n) obtained.set(n, c?.marks ?? '-'); });
                                    const subjectObtained = `${(subject.outsideObtained ?? '-')}/${(subject.subjectTotalMarks ?? '-')}`;
                                    const compCells = names.map(n => `<td style="${tdStyle}">${(obtained.get(n) ?? '-')}/${(totals.get(n) ?? '-')}</td>`).join('');
                                    const totalMarks = subject?.grandTotal ?? subject?.subjectTotalMarks ?? subject?.totalMarks ?? result?.totalMarks ?? '-';
                                    const totalObtained = subject?.grandObtained ?? subject?.obtainedMarks ?? subject?.outsideObtained ?? result?.obtainedMarks ?? '-';
                                    return `
                                        <tr>
                                            <td style="${tdStyle}">${subject?.subjectName || 'N/A'}</td>
                                            <td style="${tdStyle}">${subject?.subjectCode || 'N/A'}</td>
                                            <td style="${tdStyle}">${subjectObtained}</td>
                                            ${compCells}
                                            <td style="${tdStyle}">${totalMarks}</td>
                                            <td style="${tdStyle}">${totalObtained}</td>
                                            <td style="${tdStyle}">${subject?.grade || 'N/A'}</td>
                                            <td style="${tdStyle}">${subject?.remarks || '-'}</td>
                                        </tr>
                                    `;
                                }).join('');
                            })()}
                        </tbody>
                    </table>
                </div>
            `;
        });

        printContent += `
                <div class="footer">Generated on: ${new Date().toLocaleDateString()}</div>
            </body>
            </html>
        `;

        const printWindow = window.open('', '', 'width=800,height=600');
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.print();
    };

    const renderPagination = () => {
        const paginationItems = [];
        for (let i = 1; i <= totalPages; i++) {
            paginationItems.push(
                <PaginationItem key={i} active={i === page}>
                    <PaginationLink onClick={() => setPage(i)}>{i}</PaginationLink>
                </PaginationItem>
            );
        }

        return (
            <Pagination style={{ justifyContent: 'center' }}>
                <PaginationItem disabled={page === 1}>
                    <PaginationLink previous onClick={() => setPage(page - 1)} />
                </PaginationItem>
                {paginationItems}
                <PaginationItem disabled={page === totalPages}>
                    <PaginationLink next onClick={() => setPage(page + 1)} />
                </PaginationItem>
            </Pagination>
        );
    };

    let studentData = results?.data;

    return (
        <>
            <Header />
            <Container>
                <Row>
                    <Col>
                        <Card style={{ boxShadow: '0 4px 8px rgba(0,0,0,0.2)' }}>
                            <CardHeader style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '1.2rem' }}>
                                <h3>My Results</h3>
                                <div>
                                    <Button color="success" size="sm" onClick={generatePdfReport} disabled={loading || !studentData} className="mr-2">
                                        <FaDownload /> Download PDF
                                    </Button>
                                    <Button color="info" size="sm" onClick={handlePrint} disabled={loading || !studentData}>
                                        <FaPrint /> Print
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardBody>
                                {loading ? (
                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                        <Spinner color="primary" />
                                    </div>
                                ) : studentData ? (
                                    <>
                                        <h4 style={{ fontSize: '1.1rem' }}>Name: {studentData.fullName}</h4>
                                        <h4 style={{ fontSize: '1.1rem' }}>Registration Number: {studentData.registrationNumber}</h4>
                                        <h4 style={{ fontSize: '1.1rem' }}>Rank: {studentData.rank}</h4>
                                        {studentData.results.map((result, index) => (
                                            <Card key={index} className="mb-3" style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                                <CardHeader style={{ fontSize: '1rem' }}>
                                                    <h4>Exam Details</h4>
                                                    <h5>Exam Type: {result.type}</h5>
                                                    <h5>Year: {result.year}</h5>
                                                    <h5>Class: {result.class}</h5>
                                                    <h5>Total Marks: {result.totalMarks}</h5>
                                                    <h5>Obtained Marks: {result.obtainedMarks}</h5>
                                                    <h5>Overall Grade: {result.overallGrade}</h5>
                                                    <h5>Overall Percentage: {result.overallPercentage}</h5>
                                                </CardHeader>
                                                <CardBody>
                                                    <div className="table-responsive">
                                                        <Table className="table">
                                                            {(() => {
                                                                const isComponent = Array.isArray(result.result) && result.result.some(s => Array.isArray(s.components) && s.components.length > 0);
                                                                const componentSet = new Set();
                                                                if (isComponent) {
                                                                    result.result.forEach(subject => {
                                                                        (subject.components || []).forEach(c => {
                                                                            const name = c?.component?.name || c?.name || c?.componentName;
                                                                            if (name) componentSet.add(name);
                                                                        });
                                                                        (subject.componentObtained || []).forEach(c => {
                                                                            const name = c?.component?.name || c?.name || c?.componentName;
                                                                            if (name) componentSet.add(name);
                                                                        });
                                                                    });
                                                                }
                                                                const componentNames = Array.from(componentSet);
                                                                return (
                                                                    <>
                                                                        <thead>
                                                                            <tr style={{ backgroundColor: '#2980b9', color: 'white' }}>
                                                                                {!isComponent ? (
                                                                                    <>
                                                                                        <th>Subject</th>
                                                                                        <th>Code</th>
                                                                                        <th>Total Marks</th>
                                                                                        <th>Obtained Marks</th>
                                                                                        <th>Grade</th>
                                                                                        <th>Remarks</th>
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <th>Subject</th>
                                                                                        <th>Code</th>
                                                                                        <th>Subject Obtained</th>
                                                                                        {componentNames.map(name => (
                                                                                            <th key={`comp-h-${name}`}>{name}</th>
                                                                                        ))}
                                                                                        <th>Total Marks</th>
                                                                                        <th>Total Obtained</th>
                                                                                        <th>Grade</th>
                                                                                        <th>Remarks</th>
                                                                                    </>
                                                                                )}
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {!isComponent ? (
                                                                                result.result.map((subject, idx) => {
                                                                                    const totalMarks = subject?.grandTotal ?? subject?.subjectTotalMarks ?? subject?.totalMarks ?? result?.totalMarks ?? 'N/A';
                                                                                    const obtainedMarks = subject?.grandObtained ?? subject?.outsideObtained ?? subject?.obtainedMarks ?? result?.obtainedMarks ?? 'N/A';
                                                                                    return (
                                                                                        <tr key={subject?.subjectCode || idx}>
                                                                                            <td>{subject?.subjectName || 'N/A'}</td>
                                                                                            <td>{subject?.subjectCode || 'N/A'}</td>
                                                                                            <td>{totalMarks}</td>
                                                                                            <td>{obtainedMarks}</td>
                                                                                            <td>{subject?.grade || 'N/A'}</td>
                                                                                            <td>{subject?.remarks || 'N/A'}</td>
                                                                                        </tr>
                                                                                    );
                                                                                })
                                                                            ) : (
                                                                                result.result.map((subject, idx) => {
                                                                                    const totals = new Map();
                                                                                    (subject.components || []).forEach(c => {
                                                                                        const name = c?.component?.name || c?.name || c?.componentName;
                                                                                        if (name) totals.set(name, c?.totalMarks ?? '-');
                                                                                    });
                                                                                    const obtained = new Map();
                                                                                    (subject.componentObtained || []).forEach(c => {
                                                                                        const name = c?.component?.name || c?.name || c?.componentName;
                                                                                        if (name) obtained.set(name, c?.marks ?? '-');
                                                                                    });
                                                                                    const subjectObtained = `${(subject.outsideObtained ?? '-')}/${(subject.subjectTotalMarks ?? '-')}`;
                                                                                    const totalMarks = subject?.grandTotal ?? subject?.subjectTotalMarks ?? subject?.totalMarks ?? result?.totalMarks ?? 'N/A';
                                                                                    const totalObtained = subject?.grandObtained ?? subject?.obtainedMarks ?? subject?.outsideObtained ?? result?.obtainedMarks ?? 'N/A';
                                                                                    return (
                                                                                        <tr key={subject?.subjectCode || idx}>
                                                                                            <td>{subject?.subjectName || 'N/A'}</td>
                                                                                            <td>{subject?.subjectCode || 'N/A'}</td>
                                                                                            <td>{subjectObtained}</td>
                                                                                            {componentNames.map(name => (
                                                                                                <td key={`comp-${name}-${idx}`}>{(obtained.get(name) ?? '-')}/{(totals.get(name) ?? '-')}</td>
                                                                                            ))}
                                                                                            <td>{totalMarks}</td>
                                                                                            <td>{totalObtained}</td>
                                                                                            <td>{subject?.grade || 'N/A'}</td>
                                                                                            <td>{subject?.remarks || 'N/A'}</td>
                                                                                        </tr>
                                                                                    );
                                                                                })
                                                                            )}
                                                                        </tbody>
                                                                    </>
                                                                );
                                                            })()}
                                                        </Table>
                                                    </div>
                                                </CardBody>
                                            </Card>
                                        ))}
                                        {renderPagination()}
                                    </>
                                ) : (
                                    <p>No results found.</p>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                <Footer />
            </Container>
        </>
    );
}

export default StudentResultPage;
